import React, { useEffect, useLayoutEffect, useRef } from 'react';

import { Box, BoxProps } from '@mui/material';
import { fabric } from 'fabric';

export interface ICanvas extends Omit<BoxProps, 'ref'> {
  innerRef?: React.MutableRefObject<HTMLCanvasElement>;
  defaultOptions?: fabric.ICanvasOptions;
}

/**
 * @function
 * React Wrapper that initializes fabric.Canvas
 * @see {@link fabric.Canvas}
 */
export const Canvas = React.forwardRef<fabric.Canvas, ICanvas>(function Canvas(props, ref: React.MutableRefObject<fabric.Canvas>) {
  const { defaultOptions, innerRef, children, ...others } = props;
  const wrapperRef = useRef() as React.MutableRefObject<HTMLDivElement>;

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const canvas = document.createElement('canvas');
    wrapper.appendChild(canvas);
    const { clientWidth: width, clientHeight: height } = wrapper;

    if (innerRef) {
      innerRef.current = canvas;
    }

    const instance = new fabric.Canvas(canvas, { width, height, ...defaultOptions });
    ref.current = instance;
    return () => {
      instance.dispose();
      wrapper.removeChild(canvas);
    }
  }, [defaultOptions, innerRef, ref]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const resize = () => {
      const canvas = ref.current;
      const { clientWidth: width, clientHeight: height } = wrapper;
      if (width && height) {
        canvas.setDimensions({ width, height });
      }
    }
    const observer = new ResizeObserver(resize);
    observer.observe(wrapper);
    return () => {
      observer.unobserve(wrapper);
      observer.disconnect();
    };
  }, [ref]);

  return (
    <Box ref={wrapperRef} {...others}>
      {children}
    </Box>
  );
});
